import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useParams, useNavigate } from 'react-router-dom'
import { useAction, useMutation, useQuery } from 'convex/react'
import {
  ArrowUp,
  Check,
  Clock,
  Diamond,
  Download,
  FileEdit,
  GitBranch,
  Layers,
  Loader2,
  Maximize2,
  MessageSquare,
  Mic,
  Palette,
  PanelRightClose,
  PanelRightOpen,
  RefreshCw,
  Pause,
  Play,
  Plus,
  SkipBack,
  SkipForward,
  User as UserIcon,
  Users,
  Video,
  Wrench,
  X,
} from 'lucide-react'
import { Composer, WelcomeBlock, type AspectRatio } from '@/components/workflow/WelcomeComposer'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { track } from '@/lib/track'
import { useWorkflowChat, type ChatAttachment } from '@/hooks/useWorkflowChat'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'
import { WorkflowChat } from '@/components/workflow'
import { ExportButton, PromptShelf } from '@/components/workflow/WorkflowDocument'
import { ShareProjectButton } from '@/components/workflow/ShareProjectButton'
import { VideoPreview } from '@/components/workflow/VideoPreview'
import { Timeline } from '@/components/workflow/Timeline'
import { VoicePicker } from '@/components/workflow/VoicePicker'
import { VersionSelector } from '@/components/workflow/VersionSelector'
import { downloadVideo } from '@/lib/download'
import { Lightbox } from '@/components/ui/lightbox'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { VideoSeekProvider, useVideoSeekOptional } from '@/contexts/video-seek-context'
import { buildTimelineModel, type TimelineClip, type TimelineMode } from '@/lib/clipUtils'
import { matchPreset as matchMotionPreset } from '@/lib/motionPresets'
import AdminProjectInspectPage from '@/routes/admin/projects/inspect/[id]'

function isConvexId(param: string): boolean {
  return param.length > 20
}

function splitFilesByKind(files: File[]): { images?: File[]; audioFiles?: File[]; documents?: File[] } {
  const images: File[] = []
  const audioFiles: File[] = []
  const documents: File[] = []
  for (const f of files) {
    if (f.type.startsWith('image/') || f.type.startsWith('video/')) images.push(f)
    else if (f.type.startsWith('audio/')) audioFiles.push(f)
    else documents.push(f)
  }
  return {
    images: images.length ? images : undefined,
    audioFiles: audioFiles.length ? audioFiles : undefined,
    documents: documents.length ? documents : undefined,
  }
}

const STATUS_PILL: Record<string, { label: string; icon: React.ReactNode; classes: string }> = {
  completed:        { label: 'Done',      icon: <Check className="h-3 w-3" />,                           classes: 'bg-emerald-50 text-emerald-700' },
  generating_image: { label: 'Drafting',  icon: <Loader2 className="h-3 w-3 animate-spin" />,            classes: 'bg-amber-50 text-amber-700' },
  image_ready:      { label: 'Frame',     icon: <Check className="h-3 w-3" />,                           classes: 'bg-sky-50 text-sky-700' },
  generating_video: { label: 'Rendering', icon: <Loader2 className="h-3 w-3 animate-spin" />,            classes: 'bg-amber-50 text-amber-700' },
  failed:           { label: 'Failed',    icon: <X className="h-3 w-3" />,                               classes: 'bg-rose-50 text-rose-700' },
  pending:          { label: 'Pending',   icon: <Clock className="h-3 w-3" />,                           classes: 'bg-zinc-100 text-zinc-600' },
  cancelled:        { label: 'Cancelled', icon: <X className="h-3 w-3" />,                               classes: 'bg-zinc-100 text-zinc-500' },
}

function StatusPill({ status, className }: { status: string; className?: string }) {
  const cfg = STATUS_PILL[status] ?? STATUS_PILL.pending
  return (
    <span className={cn('inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium', cfg.classes, className)}>
      {cfg.icon}
      {cfg.label}
    </span>
  )
}

export default function WorkflowEditorV2Page() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const createWorkflow = useMutation(api.workflows.create)
  const updateWorkflow = useMutation(api.workflows.update)
  const updateAudioTrack = useMutation(api.audioGeneration.updateAudioTrackSettings)

  const isShortcode = !!(id && id !== 'new' && !isConvexId(id))
  const shortcodeWorkflow = useQuery(
    api.workflows.getByShortcode,
    isShortcode ? { shortcode: id! } : 'skip',
  )

  const [workflowId, setWorkflowId] = useState<Id<'workflows'> | null>(
    id && id !== 'new' && isConvexId(id) ? (id as Id<'workflows'>) : null,
  )
  useEffect(() => {
    if (shortcodeWorkflow && !workflowId) setWorkflowId(shortcodeWorkflow._id as Id<'workflows'>)
  }, [shortcodeWorkflow, workflowId])

  useEffect(() => {
    if (workflowId) track('project.opened', { workflowId })
  }, [workflowId])

  const workflowById = useQuery(
    api.workflows.get,
    workflowId && !isShortcode ? { id: workflowId } : 'skip',
  )
  const workflow = isShortcode ? shortcodeWorkflow : workflowById

  const clips = useQuery(
    api.workflows.getClips,
    workflowId ? { workflowId } : 'skip',
  )

  const {
    messages,
    isStreaming,
    isUploading,
    uploadStatus,
    error: chatError,
    sendMessage,
    cancelStream,
    setPendingMessage,
    hasLoaded,
  } = useWorkflowChat({ workflowId })

  const creationInitiated = useRef(false)
  const pendingMessageRef = useRef<{ msg: string; opts?: { images?: File[]; audioFiles?: File[]; documents?: File[]; attachments?: ChatAttachment[] } } | null>(null)
  useEffect(() => {
    if (workflowId && hasLoaded && pendingMessageRef.current) {
      const { msg, opts } = pendingMessageRef.current
      pendingMessageRef.current = null
      sendMessage(msg, opts)
    }
  }, [workflowId, hasLoaded, sendMessage])

  const [input, setInput] = useState('')
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedClipId) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      if (target?.closest('[data-clip-card]')) return
      setSelectedClipId(null)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [selectedClipId])
  const [tab, setTab] = useState<'clips' | 'characters' | 'timeline'>('clips')
  // Admin-only view switcher: see exactly what the user sees, or jump into
  // the observability/timeline tools without leaving the project URL.
  const [adminView, setAdminView] = useState<'user' | 'inspect'>('user')
  // Pre-open the preview pane when arriving at an existing project so it
  // doesn't animate in after load. /video/new starts collapsed.
  const [previewOpen, setPreviewOpen] = useState(!!id && id !== 'new')
  const [mobileTab, setMobileTab] = useState<'chat' | 'preview'>('chat')
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)
  const previewAutoOpened = useRef(!!id && id !== 'new')

  const handleComposerSend = useCallback((files?: File[]) => {
    const msg = input.trim()
    if (!msg && !(files && files.length)) return
    if (isStreaming) return
    setInput('')
    const opts = files && files.length ? splitFilesByKind(files) : undefined
    if (workflowId) {
      sendMessage(msg, opts)
      return
    }
    // Welcome → create workflow, queue message + files for after creation.
    if (creationInitiated.current) return
    creationInitiated.current = true
    setPendingMessage(msg)
    pendingMessageRef.current = { msg, opts }
    createWorkflow({})
      .then((result) => {
        setWorkflowId(result.id)
        window.history.replaceState(null, '', `/video/${result.shortcode}`)
      })
      .catch((error) => {
        console.error('Failed to create workflow:', error)
        creationInitiated.current = false
        pendingMessageRef.current = null
        navigate('/videos')
      })
  }, [input, isStreaming, workflowId, sendMessage, setPendingMessage, createWorkflow, navigate])

  const handleChatDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
  }, [])
  const handleChatDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])
  const handleChatDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragging(false)
    }
  }, [])
  const handleChatDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length) handleComposerSend(files)
  }, [handleComposerSend])

  // Gently open the preview pane when the conversation actually starts.
  useEffect(() => {
    if (previewAutoOpened.current) return
    if (messages.length > 0 || isStreaming) {
      previewAutoOpened.current = true
      const t = setTimeout(() => setPreviewOpen(true), 250)
      return () => clearTimeout(t)
    }
  }, [messages.length, isStreaming])

  const selectedClip = useMemo(() => {
    if (!clips) return null
    return clips.find((c) => c._id === selectedClipId) ?? clips[0] ?? null
  }, [clips, selectedClipId])

  const aspectRatio = workflow?.settings?.aspectRatio ?? '16:9'

  // Arrived via shortcode or convex id — wait for the workflow + initial
  // messages to load before painting, so we don't flash the welcome screen.
  const isExistingProject = !!id && id !== 'new'
  const isLoadingExisting = isExistingProject && (!workflowId || !hasLoaded)

  if (!user || isLoadingExisting) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-[#fafafa]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    )
  }

  const inWelcome = !workflowId && messages.length === 0 && !isStreaming

  return (
    <VideoSeekProvider>
      <Helmet><title>{workflow?.title ? `${workflow.title} — Footage` : 'Footage'}</title></Helmet>

      <div className="relative h-full w-full">
        {/* Admin view switcher floats at bottom-right across all modes so it
            stays accessible without crowding the composer header. */}
        {user?.isAdmin && (
          <AdminViewSwitcher value={adminView} onChange={setAdminView} floating />
        )}
        {adminView === 'inspect' ? (
          <div className="h-full w-full overflow-hidden bg-neutral-950">
            <AdminProjectInspectPage />
          </div>
        ) : (
      <div className="h-full w-full flex flex-col md:flex-row bg-[#fafafa] text-zinc-900">
        {/* Mobile: Share + Export buttons float into the top-right of the app header (both Chat and Preview tabs) */}
        <div className="md:hidden fixed top-2 right-3 z-50 flex items-center gap-1.5">
          <ShareProjectButton workflowId={workflowId} workflow={workflow} />
          <ExportButton
            workflowId={workflowId}
            workflow={(workflow ?? {}) as any}
            clips={clips}
          />
        </div>
        {/* Mobile tab bar — desktop uses the floating panel toggle instead */}
        <div className="md:hidden flex border-b border-zinc-200/70 bg-white shrink-0">
          <button
            type="button"
            onClick={() => setMobileTab('chat')}
            className={cn(
              'flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-[13px] font-medium transition-colors',
              mobileTab === 'chat'
                ? 'text-zinc-900 border-b-2 border-zinc-900'
                : 'text-zinc-500 border-b-2 border-transparent',
            )}
          >
            <MessageSquare className="h-4 w-4" />
            Chat
          </button>
          <button
            type="button"
            onClick={() => setMobileTab('preview')}
            className={cn(
              'flex-1 inline-flex items-center justify-center gap-2 py-2.5 text-[13px] font-medium transition-colors',
              mobileTab === 'preview'
                ? 'text-zinc-900 border-b-2 border-zinc-900'
                : 'text-zinc-500 border-b-2 border-transparent',
            )}
          >
            <Play className="h-4 w-4" />
            Preview
          </button>
        </div>

        {/* Left pane — Chat */}
        <section
          className={cn(
            'flex flex-col min-w-0 transition-[flex-basis] duration-300 relative',
            'flex-1',
            mobileTab === 'preview' && 'hidden md:flex',
            isDragging && 'ring-2 ring-inset ring-sky-400/60',
          )}
          onDragEnter={handleChatDragEnter}
          onDragOver={handleChatDragOver}
          onDragLeave={handleChatDragLeave}
          onDrop={handleChatDrop}
        >
          {/* Drop overlay */}
          {isDragging && (
            <div className="absolute inset-0 bg-sky-50/80 backdrop-blur-sm flex items-center justify-center z-40 pointer-events-none">
              <div className="text-center">
                <Plus className="h-10 w-10 text-sky-600 mx-auto mb-2" />
                <p className="text-sky-700 font-medium text-[15px]">Drop images, audio, or documents here</p>
              </div>
            </div>
          )}

          {/* Floating top-right preview pane toggle. */}
          <div className="absolute top-4 right-4 z-30 flex items-center gap-2">
            <div className="relative group hidden md:block">
              <button
                onClick={() => setPreviewOpen((o) => !o)}
                className="p-1.5 rounded-md bg-white/80 backdrop-blur-sm border border-zinc-200/70 shadow-sm hover:bg-white text-zinc-600"
              >
                {previewOpen ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
              </button>
              <div className="pointer-events-none absolute right-0 top-full mt-2 z-50 inline-flex items-center h-8 px-3 rounded-full bg-zinc-900 text-white text-[13px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                {previewOpen ? 'Close preview' : 'Open preview'}
              </div>
            </div>
          </div>

          {/* Welcome state: v2 sparkle + heading centered, v2 Composer below.
              Active state: v1 WorkflowChat (hideComposer) over v2 Composer. */}
          <div className="flex-1 min-h-0 relative">
            <div className={cn(
              'absolute inset-0 flex flex-col transition-opacity duration-200',
              inWelcome ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}>
              <div className="flex-1 min-h-0 flex flex-col justify-start pt-[18vh] md:pt-[32vh]">
                <div className="px-8">
                  <div className="max-w-[720px] mx-auto pb-[30px]">
                    <WelcomeBlock />
                  </div>
                </div>
                <div className="px-8 shrink-0">
                  <div className="max-w-[720px] mx-auto">
                    <Composer
                      value={input}
                      onChange={setInput}
                      onSend={handleComposerSend}
                      isStreaming={isStreaming}
                      onCancel={cancelStream}
                      aspectRatio={aspectRatio as AspectRatio}
                      onAspectRatioChange={(ar) => {
                        if (!workflowId) return
                        updateWorkflow({ id: workflowId, settings: { aspectRatio: ar } })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className={cn(
              'absolute inset-0 transition-opacity duration-200',
              inWelcome ? 'opacity-0 pointer-events-none' : 'opacity-100',
            )}>
              <div className="absolute inset-0">
                <ErrorBoundary level="section" resetKey={workflowId ?? undefined}>
                  <WorkflowChat
                    workflowId={workflowId}
                    messages={messages}
                    isStreaming={isStreaming}
                    isUploading={isUploading}
                    uploadStatus={uploadStatus}
                    error={chatError}
                    onSendMessage={sendMessage}
                    onCancel={cancelStream}
                    clips={clips}
                    hideComposer
                    narrowContent
                    scrollButtonClassName="left-1/2 -translate-x-1/2 w-9 h-9 bottom-[152px]"
                  />
                </ErrorBoundary>
              </div>
              {/* Composer floats over the chat. Gradient backdrop fades the
                  chat content into the page bg so messages don't bleed through
                  the area around the composer. */}
              <div className="absolute bottom-0 left-0 right-0 px-8 pt-12 pb-[22px] pointer-events-none bg-gradient-to-t from-[#fafafa] from-30% to-transparent">
                <div className="max-w-[720px] mx-auto pointer-events-auto">
                  <Composer
                    value={input}
                    onChange={setInput}
                    onSend={handleComposerSend}
                    isStreaming={isStreaming}
                    onCancel={cancelStream}
                    aspectRatio={aspectRatio as AspectRatio}
                    onAspectRatioChange={(ar) => {
                      if (!workflowId) return
                      updateWorkflow({ id: workflowId, settings: { aspectRatio: ar } })
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Right pane — Preview */}
        <aside
          className={cn(
            'flex flex-col bg-[#fafafa] overflow-hidden',
            // Mobile: full size when active, hidden when chat tab is active
            mobileTab === 'preview' ? 'flex-1 w-full min-h-0' : 'hidden',
            // Desktop: fixed-width collapsible pane
            'md:flex md:shrink-0 md:flex-none md:transition-[width,border-color] md:duration-200 md:ease-out',
            previewOpen
              ? 'md:w-[420px] [@media(min-width:1100px)]:md:w-[560px] md:border-l md:border-zinc-200/70'
              : 'md:w-0 md:border-l md:border-transparent',
          )}
          aria-hidden={!previewOpen && mobileTab !== 'preview'}
        >
          <div className="w-full md:w-[420px] [@media(min-width:1100px)]:md:w-[560px] flex flex-col h-full">
            <div className="hidden md:flex shrink-0 relative h-14 px-5 items-center justify-end gap-2 border-b border-zinc-200/70">
              <h2 className="absolute left-1/2 -translate-x-1/2 text-[15px] font-medium text-zinc-900 tracking-tight">Video Preview</h2>
              <ShareProjectButton workflowId={workflowId} workflow={workflow} />
              <ExportButton
                workflowId={workflowId}
                workflow={(workflow ?? {}) as any}
                clips={clips}
              />
            </div>
            <div className="flex-1 min-h-0 flex flex-col px-5 pt-5 pb-6 gap-4">
              <div className="shrink-0">
                <PreviewPlayer
                  clips={clips ?? []}
                  selectedClipId={selectedClipId}
                  onSelectClip={setSelectedClipId}
                  aspectRatio={aspectRatio}
                  workflow={workflow}
                  workflowId={workflowId}
                  onAudioTrackUpdate={async (trackId, startTime, duration, trimStart) => {
                    if (!workflowId) return
                    await updateAudioTrack({ workflowId, trackId, startTime, duration, ...(trimStart !== undefined && { trimStart }) })
                  }}
                />
              </div>

              <div className="shrink-0 flex items-center gap-1 p-1 rounded-xl bg-white border border-zinc-200/70 shadow-sm w-fit">
                <TabButton active={tab === 'clips'} onClick={() => setTab('clips')} icon={<Video className="h-3.5 w-3.5" />}>Clips</TabButton>
                <TabButton active={tab === 'characters'} onClick={() => setTab('characters')} icon={<Users className="h-3.5 w-3.5" />}>
                  Characters
                  {workflow?.characters && workflow.characters.length > 0 && (
                    <span className={cn(
                      'ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-medium',
                      tab === 'characters' ? 'bg-white/20 text-white' : 'bg-zinc-100 text-zinc-500',
                    )}>
                      {workflow.characters.length}
                    </span>
                  )}
                </TabButton>
                <TabButton active={tab === 'timeline'} onClick={() => setTab('timeline')} icon={<Layers className="h-3.5 w-3.5" />}>Timeline</TabButton>
              </div>

              {tab === 'clips' ? (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <ClipListWithSeek
                    clips={workflowId ? clips : []}
                    characters={workflow?.characters as WorkflowCharacter[] | undefined}
                    timelineMode={workflow?.timelineMode}
                    selectedId={selectedClipId}
                    onSelect={setSelectedClipId}
                  />
                </div>
              ) : tab === 'characters' ? (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <CharacterList
                    workflowId={workflowId}
                    characters={workflow?.characters as WorkflowCharacter[] | undefined}
                  />
                </div>
              ) : (
                <div className="flex-1 min-h-0">
                  <ReadOnlyTimeline
                    workflowId={workflowId}
                    clips={(workflowId ? clips : []) ?? []}
                    workflow={workflow}
                    selectedClipId={selectedClip?._id ?? null}
                    onSelectClip={setSelectedClipId}
                  />
                </div>
              )}
            </div>
          </div>
        </aside>

      </div>
        )}
      </div>
    </VideoSeekProvider>
  )
}

function AdminViewSwitcher({
  value,
  onChange,
  floating = false,
}: {
  value: 'user' | 'inspect'
  onChange: (v: 'user' | 'inspect') => void
  floating?: boolean
}) {
  const options: { id: 'user' | 'inspect'; label: string; icon: React.ReactNode }[] = [
    { id: 'user', label: 'User', icon: <UserIcon className="h-3.5 w-3.5" /> },
    { id: 'inspect', label: 'Inspect', icon: <Wrench className="h-3.5 w-3.5" /> },
  ]
  return (
    <div
      className={cn(
        'hidden md:flex items-center gap-0.5 p-0.5 rounded-full bg-white/90 backdrop-blur-sm border border-red-300/80 shadow-sm ring-1 ring-red-500/20',
        floating && 'absolute bottom-4 right-4 z-50',
      )}
      title="Admin view (admins only)"
    >
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          className={cn(
            'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[12px] font-medium transition-colors',
            value === opt.id
              ? 'bg-red-600 text-white shadow-sm'
              : 'text-red-600/80 hover:text-red-700 hover:bg-red-50',
          )}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  )
}

/* ────── Subcomponents ────── */



export function TabButton({
  active,
  onClick,
  icon,
  children,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-[12px] font-medium transition-colors',
        active ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-900',
      )}
    >
      {icon}
      {children}
    </button>
  )
}

type Clip = {
  _id: string
  title?: string
  description?: string
  videoPrompt?: string
  promptSummary?: string
  duration: number
  videoStatus: string
  videoUrl?: string
  firstFrameUrl?: string
  startKeyframeUrl?: string
  endKeyframeUrl?: string
  storyboardImageUrl?: string
  referenceFrameUrl?: string
  videoVersionCount?: number
  orderIndex: number
  charactersInClip?: string[]
  transition?: { type: string; duration: number }
  generationMode?: string
  transcriptionStatus?: string
  transcription?: { text: string }
  effects?: {
    colorGrade?: string
    letterbox?: boolean
    fadeIn?: { color: string; duration: number }
    fadeOut?: { color: string; duration: number }
    vignette?: number
    kenBurns?: { startScale: number; endScale: number; startX?: number; startY?: number; endX?: number; endY?: number }
  }
}

function getClipPreviewImage(clip: Clip | null | undefined): string | undefined {
  if (!clip) return undefined
  return (
    clip.firstFrameUrl
    || clip.storyboardImageUrl
    || clip.startKeyframeUrl
    || clip.referenceFrameUrl
    || clip.endKeyframeUrl
    || undefined
  )
}

export function PreviewPlayer({
  clips,
  selectedClipId,
  onSelectClip,
  aspectRatio,
  workflow,
  onAudioTrackUpdate,
}: {
  clips: Clip[]
  selectedClipId: string | null
  onSelectClip: (id: string | null) => void
  aspectRatio: string
  workflow: any
  workflowId: Id<'workflows'> | null
  onAudioTrackUpdate?: (trackId: string, startTime: number, duration: number, trimStart?: number) => Promise<void>
}) {
  // VideoPreview registers its native-fullscreen toggle here so the button
  // overlaid on the player card can invoke it.
  const fullscreenRef = useRef<(() => void) | null>(null)
  const seek = useVideoSeekOptional()
  const aspect = aspectRatio === '9:16'
    ? 'aspect-[9/16]'
    : aspectRatio === '1:1'
      ? 'aspect-square'
      : aspectRatio === '4:5'
        ? 'aspect-[4/5]'
        : aspectRatio === '4:3'
          ? 'aspect-[4/3]'
          : 'aspect-video'
  // Cap card width so portrait aspects don't make the card taller than the pane.
  const cardMaxWidth = aspectRatio === '9:16'
    ? 'max-w-[220px]'
    : aspectRatio === '4:5'
      ? 'max-w-[300px]'
      : aspectRatio === '1:1'
        ? 'max-w-[340px]'
        : ''

  const activeIndex = useMemo(() => {
    if (!selectedClipId) return 0
    const i = clips.findIndex((c) => c._id === selectedClipId)
    return i >= 0 ? i : 0
  }, [clips, selectedClipId])

  // VideoPreview treats clips with storyboard / keyframe / first-frame images
  // as playable too (storyboard mode), so we delegate to it whenever any clip
  // exists. We also delegate when a voiceover/music track is ready before any
  // clip — VideoPreview mounts the player so the audio can be auditioned. The
  // blue placeholder is only for the empty-project (no clips, no audio) state.
  const hasReadyAudio = (workflow?.audioTracks ?? []).some(
    (t: { status?: string; url?: string }) => t.status === 'ready' && t.url,
  )
  const hasPlayable = clips.length > 0 || hasReadyAudio
  const placeholderClip = clips[activeIndex] ?? clips[0] ?? null
  const placeholderThumb = getClipPreviewImage(placeholderClip)
  const placeholderVersion = placeholderClip?.videoVersionCount ?? 1
  const placeholderIndex = (placeholderClip?.orderIndex ?? 0) + 1

  // Pre-production poster: before anything is playable, mirror the newest
  // casting / location visual so the rail shows the movie taking shape
  // instead of an empty placeholder.
  const preproductionPoster = useMemo(() => {
    if (hasPlayable) return null
    const images: { url: string; generatedAt: number; label: string }[] = []
    for (const c of workflow?.characters ?? []) {
      for (const img of c.referenceImages ?? []) {
        if (img.url) images.push({ url: img.url, generatedAt: img.generatedAt ?? 0, label: c.name })
      }
    }
    for (const l of workflow?.locations ?? []) {
      for (const img of l.referenceImages ?? []) {
        if (img.url) images.push({ url: img.url, generatedAt: img.generatedAt ?? 0, label: l.name })
      }
    }
    if (images.length === 0) return null
    images.sort((a, b) => b.generatedAt - a.generatedAt)
    return images[0]
  }, [hasPlayable, workflow?.characters, workflow?.locations])

  const timelineModel = useMemo(
    () => buildTimelineModel(clips as unknown as TimelineClip[], { mode: workflow?.timelineMode }),
    [clips, workflow?.timelineMode],
  )
  const activeClipStart = timelineModel.entries[activeIndex]?.globalStart ?? 0
  const atClipStart = Math.abs((seek?.currentTime ?? 0) - activeClipStart) < 0.15
  const showPoster = clips.length > 0 && !!placeholderThumb && !seek?.isPlaying && atClipStart

  return (
    <div className={cn('rounded-2xl border border-zinc-200/70 bg-white overflow-hidden shadow-sm mx-auto', cardMaxWidth)}>
      <div className={cn('relative w-full', aspect)}>
        <div className="absolute inset-0">
          {hasPlayable ? (
            <ErrorBoundary level="section" resetKey={String(clips.length)}>
              <VideoPreview
                scenes={clips as any}
                activeSceneIndex={activeIndex}
                onSceneChange={(i) => {
                  const c = clips[i]
                  if (c) onSelectClip(c._id)
                }}
                audioTracks={workflow?.audioTracks}
                textOverlays={workflow?.textOverlays}
                demoStyle={workflow?.demoStyle}
                onAudioTrackUpdate={onAudioTrackUpdate}
                aspectRatio={aspectRatio}
                timelineMode={workflow?.timelineMode}
                hideFullscreenButton
                onRegisterFullscreen={(fn) => { fullscreenRef.current = fn }}
              />
              {showPoster && (
                <img
                  src={placeholderThumb}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover pointer-events-none z-10"
                />
              )}
              <button
                type="button"
                onClick={() => fullscreenRef.current?.()}
                title="Fullscreen"
                aria-label="Fullscreen"
                className="absolute top-2 right-2 z-20 p-1.5 rounded-lg bg-black/40 hover:bg-black/60 text-white/90 backdrop-blur-sm transition-colors"
              >
                <Maximize2 className="h-4 w-4" />
              </button>
            </ErrorBoundary>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-sky-200 via-sky-300 to-sky-500">
              {(preproductionPoster || placeholderThumb) && (
                <img src={preproductionPoster?.url || placeholderThumb} alt="" className="absolute inset-0 w-full h-full object-cover" />
              )}
              {preproductionPoster && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              )}
              <div className="absolute bottom-3 left-[26px] text-white">
                <div className="text-[10px] font-mono tracking-wider text-white/70 uppercase">
                  {preproductionPoster ? (
                    <>Pre-production <span className="mx-1">·</span> {preproductionPoster.label}</>
                  ) : (
                    <>Clip {String(placeholderIndex).padStart(2, '0')} <span className="mx-1">·</span> v{placeholderVersion}</>
                  )}
                </div>
                <div className="text-[18px] font-medium leading-tight">
                  {placeholderClip?.title || workflow?.title || 'Untitled'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <PlayBar
        clips={clips}
        timelineMode={workflow?.timelineMode}
        onSelectClip={onSelectClip}
        flush
        stacked={aspectRatio === '9:16' || aspectRatio === '4:5'}
      />
    </div>
  )
}

export function ClipListWithSeek({
  clips,
  characters,
  timelineMode,
  selectedId,
  onSelect,
}: {
  clips: Clip[] | undefined
  characters: WorkflowCharacter[] | undefined
  timelineMode?: TimelineMode
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const seek = useVideoSeekOptional()
  const handleSelect = useCallback((clipId: string) => {
    onSelect(clipId)
    if (!seek || !clips) return
    const model = buildTimelineModel(clips as unknown as TimelineClip[], { mode: timelineMode })
    const entry = model.entries.find((e) => String(e.clip._id) === clipId)
    if (entry) seek.seekTo(entry.globalStart)
  }, [seek, clips, timelineMode, onSelect])
  return <ClipList clips={clips} characters={characters} selectedId={selectedId} onSelect={handleSelect} />
}

function ClipList({
  clips,
  characters,
  selectedId,
  onSelect,
}: {
  clips: Clip[] | undefined
  characters: WorkflowCharacter[] | undefined
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  if (clips === undefined) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    )
  }
  if (clips.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-200 p-8 text-center text-[12px] text-zinc-400">
        No clips yet. Ask the agent to start.
      </div>
    )
  }
  return (
    <div className="space-y-1.5">
      {clips.map((c, i) => (
        <ClipRow
          key={c._id}
          clip={c}
          characters={characters}
          index={i + 1}
          selected={selectedId === c._id}
          onClick={() => onSelect(c._id)}
        />
      ))}
    </div>
  )
}

function ClipRow({ clip, characters, index, selected, onClick }: { clip: Clip; characters: WorkflowCharacter[] | undefined; index: number; selected: boolean; onClick: () => void }) {
  const status = clip.videoStatus || 'pending'
  const thumb = getClipPreviewImage(clip)
  const version = clip.videoVersionCount ?? 1
  const [showShelf, setShowShelf] = useState(false)
  const clipChars = useMemo(() => {
    if (!characters || !clip.charactersInClip?.length) return []
    return clip.charactersInClip
      .map((id) => characters.find((c) => c.id === id))
      .filter((c): c is WorkflowCharacter => !!c)
  }, [characters, clip.charactersInClip])
  return (
    <div
      role="button"
      tabIndex={0}
      data-clip-card
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn(
        'group w-full flex items-stretch rounded-xl border bg-white overflow-hidden text-left transition-all cursor-pointer',
        selected
          ? 'border-primary'
          : 'border-zinc-200/60 hover:border-zinc-300/80',
      )}
    >
      <div className="relative shrink-0 w-32 h-20 bg-gradient-to-br from-zinc-200 to-zinc-400">
        {thumb && <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover" />}

        {clip.videoUrl && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              downloadVideo(clip.videoUrl!, `${clip.title || `clip-${index}`}.mp4`).catch((err) =>
                console.error('Download failed:', err),
              )
            }}
            title="Download clip"
            className="absolute top-1 right-1 z-10 p-1 rounded bg-black/55 hover:bg-black/75 text-white opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Download size={11} />
          </button>
        )}

        <div className="absolute bottom-1 left-1 z-10 flex items-center gap-1">
          {clip.generationMode && (() => {
            const modeConfig: Record<string, { label: string; bg: string; icon: React.ReactNode }> = {
              'text-to-video':         { label: 'Text to Video',          bg: 'bg-neutral-500/70',  icon: <Layers className="h-2.5 w-2.5" /> },
              'image-to-video':        { label: 'Image to Video',         bg: 'bg-blue-500/70',     icon: <Layers className="h-2.5 w-2.5" /> },
              'reference-to-video':    { label: 'Character Reference',    bg: 'bg-emerald-500/70',  icon: <UserIcon className="h-2.5 w-2.5" /> },
              'first-last-frame':      { label: 'Keyframe Transition',    bg: 'bg-purple-500/70',   icon: <Diamond className="h-2.5 w-2.5" /> },
              'edit-video':            { label: 'Edit Video',             bg: 'bg-amber-500/70',    icon: <Layers className="h-2.5 w-2.5" /> },
              'video-reference':       { label: 'Video Style Reference',  bg: 'bg-teal-500/70',     icon: <Video className="h-2.5 w-2.5" /> },
              'avatar':                { label: 'Avatar',                 bg: 'bg-violet-500/70',   icon: <Mic className="h-2.5 w-2.5" /> },
              'uploaded':              { label: 'Uploaded',               bg: 'bg-stone-500/70',    icon: <ArrowUp className="h-2.5 w-2.5" /> },
            }
            const cfg = modeConfig[clip.generationMode]
            if (!cfg) return null
            return (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={cn('inline-flex items-center justify-center h-[14px] w-[14px] rounded text-white', cfg.bg)}>
                      {cfg.icon}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-[11px]">{cfg.label}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )
          })()}
          {clip.transcriptionStatus === 'ready' && clip.transcription?.text && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center justify-center h-[14px] w-[14px] rounded bg-sky-500/70 text-white">
                    <MessageSquare className="h-2.5 w-2.5" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px] text-[11px] leading-relaxed">
                  {clip.transcription.text}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {clipChars.length > 0 && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'flex items-center -space-x-1.5',
                      clip.generationMode === 'reference-to-video' && 'ring-1 ring-emerald-400 rounded-full',
                    )}
                  >
                    {clipChars.slice(0, 3).map((char) => {
                      const imgUrl = char.referenceImages?.[0]?.url
                      return imgUrl ? (
                        <img
                          key={char.id}
                          src={imgUrl}
                          alt={char.name}
                          className="w-[14px] h-[14px] rounded-full object-cover border border-white/70 shadow-sm"
                        />
                      ) : (
                        <span
                          key={char.id}
                          className="w-[14px] h-[14px] rounded-full bg-zinc-700 flex items-center justify-center text-white text-[7px] font-medium border border-white/70 shadow-sm"
                        >
                          {char.name.charAt(0).toUpperCase()}
                        </span>
                      )
                    })}
                    {clipChars.length > 3 && (
                      <span className="w-[14px] h-[14px] rounded-full bg-zinc-600 flex items-center justify-center text-white text-[7px] font-medium border border-white/70 shadow-sm">
                        +{clipChars.length - 3}
                      </span>
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-[11px]">
                  {clip.generationMode === 'reference-to-video'
                    ? `Character Reference: ${clipChars.map((c) => c.name).join(', ')}`
                    : `Characters: ${clipChars.map((c) => c.name).join(', ')}`}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>

        {version > 1 ? (
          <VersionSelector
            clipId={clip._id as Id<'workflowClips'>}
            versionCount={version}
            className="absolute bottom-1 right-1 z-10 inline-flex items-center gap-0.5 h-[16px] px-1 rounded bg-black/55 hover:bg-black/75 text-white text-[9px] font-medium transition-colors"
          >
            <GitBranch size={8} />
            <span>v{version}</span>
          </VersionSelector>
        ) : (
          <span className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 h-[16px] px-1 rounded bg-black/40 text-white text-[9px] font-medium">
            v{version}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 flex flex-col justify-center px-3 py-2 gap-1">
        <div className="text-[13px] font-medium text-zinc-900 leading-tight truncate">
          {index}. {clip.title || 'Untitled'}
        </div>
        <div className="flex items-center gap-1 flex-wrap text-[10px] text-zinc-500">
          {clip.effects?.colorGrade && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">
              <Palette className="h-2.5 w-2.5" />
              {clip.effects.colorGrade}
            </span>
          )}
          {clip.effects?.letterbox && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">letterbox</span>
          )}
          {clip.effects?.fadeIn && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">fade in</span>
          )}
          {clip.effects?.fadeOut && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">fade out</span>
          )}
          {clip.effects?.vignette != null && clip.effects.vignette > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">vignette</span>
          )}
          {clip.effects?.kenBurns && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">
              {matchMotionPreset(clip.effects.kenBurns)?.name ?? 'motion'}
            </span>
          )}
          {clip.transition && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-zinc-100 text-zinc-600">
              <Diamond className="h-2.5 w-2.5" />
              <span className="capitalize">{clip.transition.type}</span>
              <span className="text-zinc-400">({clip.transition.duration}s)</span>
            </span>
          )}
        </div>
      </div>
      <StatusPill status={status} className="shrink-0 self-center ml-1.5" />
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setShowShelf(true)
              }}
              className="shrink-0 self-center mr-2 ml-1 p-1.5 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <FileEdit size={15} />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left" className="text-[11px]">View prompt</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {showShelf && (
        <PromptShelf
          scene={clip as unknown as Parameters<typeof PromptShelf>[0]['scene']}
          characters={characters}
          onClose={() => setShowShelf(false)}
        />
      )}
    </div>
  )
}

function PlayBar({
  clips,
  timelineMode,
  onSelectClip,
  flush = false,
  stacked = false,
}: {
  clips: Clip[]
  timelineMode?: TimelineMode
  onSelectClip: (id: string | null) => void
  flush?: boolean
  stacked?: boolean
}) {
  const seek = useVideoSeekOptional()
  const model = useMemo(
    () => buildTimelineModel(clips as unknown as TimelineClip[], { mode: timelineMode }),
    [clips, timelineMode],
  )
  const total = model.totalDuration
  const current = seek?.currentTime ?? 0
  const isPlaying = seek?.isPlaying ?? false
  const barRef = useRef<HTMLDivElement>(null)

  const fmt = (s: number) => {
    if (!Number.isFinite(s)) return '0:00'
    const m = Math.floor(s / 60)
    const r = Math.floor(s % 60)
    return `${m}:${r.toString().padStart(2, '0')}`
  }

  const handleSeekFromPointer = (clientX: number) => {
    const bar = barRef.current
    if (!bar || total <= 0) return
    const rect = bar.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    seek?.seekTo(ratio * total)
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!seek) return
    seek.scrubStart()
    handleSeekFromPointer(e.clientX)
    const onMove = (ev: PointerEvent) => {
      const bar = barRef.current
      if (!bar) return
      const rect = bar.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width))
      seek.scrubTo(ratio * total)
    }
    const onUp = () => {
      seek.scrubEnd()
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const goPrev = () => {
    const entries = model.entries
    let target = entries[0]
    for (const e of entries) {
      if (e.globalStart + 0.05 < current) target = e
      else break
    }
    if (target) {
      seek?.seekTo(target.globalStart)
      onSelectClip(String(target.clip._id))
    }
  }

  const goNext = () => {
    const entries = model.entries
    const next = entries.find((e) => e.globalStart > current + 0.05)
    if (next) {
      seek?.seekTo(next.globalStart)
      onSelectClip(String(next.clip._id))
    }
  }

  if (!seek) return null
  const empty = total <= 0

  const scrubber = (
    <div
      ref={barRef}
      onPointerDown={empty ? undefined : onPointerDown}
      className={cn(
        'relative h-2 rounded-full overflow-hidden touch-none select-none',
        stacked ? 'w-full' : 'flex-1',
        empty ? 'cursor-default bg-zinc-100' : 'cursor-pointer',
      )}
    >
      {!empty && (
        <>
          <div className="absolute inset-0 flex gap-[2px]">
            {model.entries.map((entry) => (
              <div
                key={String(entry.clip._id)}
                className="h-full bg-zinc-200"
                style={{ flexBasis: `${(entry.effectiveDuration / total) * 100}%` }}
              />
            ))}
          </div>
          <div
            className="absolute inset-y-0 left-0 bg-sky-500/80"
            style={{ width: `${(current / total) * 100}%` }}
          />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-sky-600 shadow"
            style={{ left: `${(current / total) * 100}%` }}
          />
        </>
      )}
    </div>
  )

  const prevButton = (
    <button
      type="button"
      onClick={empty ? undefined : goPrev}
      disabled={empty}
      className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-700 hover:bg-zinc-100 disabled:text-zinc-300 disabled:hover:bg-transparent"
    >
      <SkipBack className="h-3.5 w-3.5" />
    </button>
  )
  const playButton = (
    <button
      type="button"
      onClick={empty ? undefined : () => seek.togglePlayPause()}
      disabled={empty}
      className="w-8 h-8 rounded-md flex items-center justify-center text-zinc-900 hover:bg-zinc-100 disabled:text-zinc-300 disabled:hover:bg-transparent"
    >
      {isPlaying ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
    </button>
  )
  const nextButton = (
    <button
      type="button"
      onClick={empty ? undefined : goNext}
      disabled={empty}
      className="w-7 h-7 rounded-md flex items-center justify-center text-zinc-700 hover:bg-zinc-100 disabled:text-zinc-300 disabled:hover:bg-transparent"
    >
      <SkipForward className="h-3.5 w-3.5" />
    </button>
  )
  const currentLabel = (
    <span className="text-[11px] font-mono tabular-nums text-zinc-500 select-none">{fmt(current)}</span>
  )
  const totalLabel = (
    <span className="text-[11px] font-mono tabular-nums text-zinc-500 select-none">{fmt(total)}</span>
  )

  const containerClasses = cn(
    'px-3 py-2.5',
    flush
      ? 'border-t border-zinc-200/70 bg-white'
      : 'rounded-xl border border-zinc-200/70 bg-white shadow-sm',
  )

  if (stacked) {
    return (
      <div className={cn(containerClasses, 'flex flex-col gap-2')}>
        {scrubber}
        <div className="flex items-center gap-3">
          {prevButton}
          {playButton}
          {nextButton}
          <div className="flex-1" />
          {currentLabel}
          {totalLabel}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(containerClasses, 'flex items-center gap-3')}>
      {prevButton}
      {playButton}
      {nextButton}
      {currentLabel}
      {scrubber}
      {totalLabel}
    </div>
  )
}

type CharacterReferenceImage = { url: string; pose: string; generatedAt?: number }

type WorkflowCharacter = {
  id: string
  name: string
  description: string
  referenceImages?: CharacterReferenceImage[]
  referenceStatus?: 'pending' | 'generating' | 'completed' | 'failed'
  voiceId?: string
  voiceProvider?: string
  klingVoiceStatus?: 'none' | 'creating' | 'ready' | 'failed'
}

function CharacterList({
  workflowId,
  characters,
}: {
  workflowId: Id<'workflows'> | null
  characters: WorkflowCharacter[] | undefined
}) {
  const updateCharacter = useMutation(api.workflows.updateCharacter)
  const regenerateCharacterRefs = useAction(api.workflows.regenerateCharacterReferences)
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<{ url: string; name: string; pose: string } | null>(null)

  if (!workflowId) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-200 p-8 text-center text-[12px] text-zinc-400">
        Characters appear once your project starts.
      </div>
    )
  }
  if (characters === undefined) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-400">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    )
  }
  if (characters.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-200 p-8 text-center text-[12px] text-zinc-400">
        No characters yet. The agent will introduce them as your story grows.
      </div>
    )
  }

  const handleRegenerate = async (characterId: string) => {
    setRegeneratingId(characterId)
    try {
      await regenerateCharacterRefs({ workflowId, characterId })
    } catch (error) {
      console.error('Failed to regenerate character references:', error)
    } finally {
      setRegeneratingId(null)
    }
  }

  return (
    <>
      <div className="space-y-3">
        {characters.map((char) => {
          const isRegenerating = regeneratingId === char.id || char.referenceStatus === 'generating'
          const primaryImage = char.referenceImages?.[0]
          return (
            <div
              key={char.id}
              className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm overflow-hidden"
            >
              {/* Character header — pill-style avatar + name */}
              <div className="flex items-center gap-2 px-4 pt-3.5">
                {primaryImage?.url ? (
                  <img
                    src={primaryImage.url}
                    alt={char.name}
                    className="w-6 h-6 rounded-full object-cover ring-1 ring-zinc-200"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 font-medium text-[10px]">
                    {char.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="text-[13px] font-medium text-zinc-900">{char.name || 'Untitled'}</span>
              </div>

              <div className="flex gap-3 p-4">
                {/* Image with pose label */}
                <div className="shrink-0 flex flex-col items-center gap-1.5">
                  {primaryImage?.url ? (
                    <button
                      onClick={() => setLightbox({ url: primaryImage.url, name: char.name, pose: primaryImage.pose })}
                      className="block cursor-zoom-in hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={primaryImage.url}
                        alt={char.name}
                        className="w-20 h-20 rounded-lg object-cover border border-zinc-200"
                      />
                    </button>
                  ) : isRegenerating ? (
                    <div className="w-20 h-20 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-500 font-medium text-2xl">
                      {char.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  {primaryImage?.pose && (
                    <span className="text-[10px] font-medium text-zinc-500 bg-zinc-100 rounded px-1.5 py-0.5 capitalize">
                      {primaryImage.pose.replace('_', ' ')}
                    </span>
                  )}
                </div>

                {/* Editable fields */}
                <div className="flex-1 min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      defaultValue={char.name}
                      onBlur={(e) => {
                        const v = e.target.value.trim()
                        if (v && v !== char.name) {
                          updateCharacter({ workflowId, characterId: char.id, name: v })
                        }
                      }}
                      placeholder="Character name"
                      className="flex-1 min-w-0 text-[13px] font-medium text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-1.5 outline-none focus:border-zinc-300 focus:bg-white transition-colors"
                    />
                    <button
                      onClick={() => handleRegenerate(char.id)}
                      disabled={isRegenerating}
                      className={cn(
                        'shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[11px] font-medium transition-colors',
                        isRegenerating
                          ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                          : 'bg-zinc-900 text-white hover:bg-zinc-800',
                      )}
                    >
                      {isRegenerating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                      <span>{isRegenerating ? 'Regen' : (char.referenceImages?.length ? 'Regen' : 'Gen')}</span>
                    </button>
                  </div>
                  <textarea
                    defaultValue={char.description}
                    onBlur={(e) => {
                      const v = e.target.value.trim()
                      if (v !== char.description) {
                        updateCharacter({ workflowId, characterId: char.id, description: v })
                      }
                    }}
                    rows={3}
                    placeholder="Physical description for video generation…"
                    className="w-full text-[12px] text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2 outline-none focus:border-zinc-300 focus:bg-white resize-none transition-colors leading-relaxed"
                  />
                </div>
              </div>

              {/* Voice row */}
              <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-zinc-100 bg-zinc-50/40">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                  <Mic className="h-3 w-3" />
                  Voice
                </span>
                <VoicePicker
                  currentVoiceId={char.voiceId}
                  onVoiceSelect={(voiceId) =>
                    updateCharacter({ workflowId, characterId: char.id, voiceId, voiceProvider: 'dia' })
                  }
                  onRemoveVoice={() =>
                    updateCharacter({ workflowId, characterId: char.id, clearVoice: true })
                  }
                  klingVoiceStatus={char.klingVoiceStatus}
                />
              </div>
            </div>
          )
        })}
      </div>
      <Lightbox url={lightbox?.url ?? null} onClose={() => setLightbox(null)} />
    </>
  )
}

function ReadOnlyTimeline({
  clips,
  workflow,
  selectedClipId,
  onSelectClip,
}: {
  workflowId?: Id<'workflows'> | null
  clips: Clip[]
  workflow: any
  selectedClipId: string | null
  onSelectClip: (id: string | null) => void
}) {
  const seek = useVideoSeekOptional()
  const audioTracks = workflow?.audioTracks ?? []
  // Audio that's ready before any clip exists should still be auditionable in
  // the read-only timeline (the agent's "approve the audio first" step). The
  // Timeline component already renders audio rows + a playhead with zero
  // scenes; we just stop gating the whole timeline on clips and derive a
  // duration from the audio when no clips are present.
  const maxAudioEnd = useMemo(
    () =>
      audioTracks
        .filter((t: { status?: string; url?: string }) => t.status === 'ready' && t.url)
        .reduce((m: number, t: { startTime: number; duration: number }) => Math.max(m, t.startTime + t.duration), 0),
    [audioTracks],
  )
  const totalDuration = useMemo(
    () =>
      Math.max(
        buildTimelineModel(clips as unknown as TimelineClip[], { mode: workflow?.timelineMode }).totalDuration,
        maxAudioEnd,
      ),
    [clips, workflow?.timelineMode, maxAudioEnd],
  )
  const selectedIndex = useMemo(() => {
    const i = clips.findIndex((c) => c._id === selectedClipId)
    return i >= 0 ? i : undefined
  }, [clips, selectedClipId])

  if (!seek || (clips.length === 0 && maxAudioEnd === 0)) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-200 p-8 text-center text-[12px] text-zinc-400">
        Timeline appears once clips are created
      </div>
    )
  }

  return (
    <div
      className={cn(
        'v2-readonly-timeline min-h-[340px] rounded-xl overflow-hidden border border-zinc-200/70 bg-white shadow-sm',
        // Hide the editing-only split-at-playhead button. The Ripple/Fixed
        // toggle is hidden by passing workflowId={undefined} below. The
        // zoom-controls block is hidden via index.css under the
        // .v2-readonly-timeline scope (:has() inside Tailwind arbitrary
        // variants doesn't roundtrip cleanly with attribute values).
        '[&_button[title^="Split"]]:hidden',
      )}
      style={{
        // Re-theme the v1 Timeline (which is dark-mode-only) to v2's light
        // palette by remapping the --surface-* CSS variables. Both the
        // Tailwind classes (bg-surface, text-surface-*, etc.) and the inline
        // var(--surface-*) styles inside Timeline will pick these up.
        ['--surface' as any]: 'oklch(1 0 0)',                  // white
        ['--surface-foreground' as any]: 'oklch(0.205 0 0)',   // near-black text
        ['--surface-elevated' as any]: 'oklch(0.97 0 0)',      // very light gray surface
        ['--surface-muted' as any]: 'oklch(0.556 0 0)',        // mid-gray secondary text
        ['--surface-border' as any]: 'oklch(0.92 0 0)',        // light hairline borders
        // Keep the accent/playhead colors so the playhead/highlights still pop
      } as React.CSSProperties}
    >
      <ErrorBoundary level="section" resetKey={String(clips.length)}>
        {/* Read-only: omit edit handlers (delete, reorder, trim, audio/text
            update). Keep navigation/seek/scene change. Pointer events on
            mutating regions are blocked further with a pointer-events shield
            on internal drag handles via CSS but Timeline gracefully no-ops
            when handlers are absent. workflowId omitted so Timeline hides
            the Ripple/Fixed mode toggle. */}
        <Timeline
          scenes={clips as any}
          audioTracks={audioTracks}
          textOverlays={workflow?.textOverlays}
          timelineMode={workflow?.timelineMode}
          currentTime={seek.currentTime}
          totalDuration={totalDuration}
          onSeek={(t) => seek.seekTo(t)}
          onScrubStart={() => seek.scrubStart()}
          onScrub={(t) => seek.scrubTo(t)}
          onScrubEnd={() => seek.scrubEnd()}
          currentSceneIndex={selectedIndex}
          onSceneChange={(i) => {
            const c = clips[i]
            if (c) onSelectClip(c._id)
          }}
          onPlayPause={() => seek.togglePlayPause()}
          isPlaying={seek.isPlaying}
          autoFitWidth
        />
      </ErrorBoundary>
    </div>
  )
}
